import { HttpMethod, defaultHeaders } from '../constants';
import {
  Rfi,
  RfiId,
  SuggestedCustomIdentifierResponse,
  CreateRfiRequest,
  GetRfisRequest,
  UpdateRfiRequest,
  GetRfisResponse,
  CommentsList,
  Comment,
  GetCommentsRequest,
  GetAttachmentsRequest,
  AttachmentList,
  CreateAttachmentRequest,
  Attachment,
  ListChangesetResponse,
  CreateLinkRequest,
  CreateLinkResponse,
  RemoveLinkRequestDefinition,
  RemoveLinkResponse,
  GetChangesetsRequest,
  NextCustomIdentifierResponseDefinition,
} from '../types';
import { constructFilterParamList, getFlatParams } from '../utils/fetchHelpers';
import { ApiConfig } from './types';

const RFIsApi = ({ baseUrl, fetchWithToken }: ApiConfig) => {
  return {
    get(id: RfiId): Promise<Rfi> {
      const url = `${baseUrl}/rfis/${id}`;
      return fetchWithToken<Rfi>(url);
    },
    getAll(getRfiRequest: GetRfisRequest): Promise<GetRfisResponse> {
      const { limit, offset, sortBy, filterBy, fields } = getRfiRequest;
      const url = `${baseUrl}/rfis`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (sortBy) {
        searchParams.set('sort', getFlatParams(sortBy));
      }
      if (filterBy) {
        const filterParamList = constructFilterParamList(filterBy);
        filterParamList.forEach(([param, value]) => {
          searchParams.set(param, value);
        });
      }
      if (fields) {
        searchParams.set('fields', getFlatParams(fields));
      }
      const search = searchParams.toString();
      return fetchWithToken<GetRfisResponse>(search ? `${url}?${search}` : url);
    },
    customIdentifier: {
      getSuggested(): Promise<SuggestedCustomIdentifierResponse> {
        const url = `${baseUrl}/rfis/custom-identifier`;
        return fetchWithToken<SuggestedCustomIdentifierResponse>(url).then((res) => res);
      },
      validate(customIdentifier: string): Promise<boolean> {
        const url = `${baseUrl}/rfis/custom-identifier:validate`;

        return fetchWithToken(url, {
          method: HttpMethod.Post,
          body: JSON.stringify({ customIdentifier }),
          headers: defaultHeaders,
        })
          .then(() => Promise.resolve(true))
          .catch(() => Promise.resolve(false));
      },
      getRevision(rfiId: RfiId): Promise<NextCustomIdentifierResponseDefinition> {
        const url = `${baseUrl}/rfis:next-revision`;
        return fetchWithToken<NextCustomIdentifierResponseDefinition>(url, {
          method: HttpMethod.Post,
          body: JSON.stringify({ rfiId }),
          headers: defaultHeaders,
        });
      },
    },
    create(rfiAttributes: CreateRfiRequest): Promise<Rfi> {
      const url = `${baseUrl}/rfis`;
      return fetchWithToken<Rfi>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(rfiAttributes),
        headers: defaultHeaders,
      });
    },
    update(id: string, body: UpdateRfiRequest): Promise<Rfi> {
      const url = `${baseUrl}/rfis/${id}`;
      return fetchWithToken<Rfi>(url, {
        method: HttpMethod.Patch,
        body: JSON.stringify(body),
        headers: defaultHeaders,
      });
    },
    getComments(getCommentsRequest: GetCommentsRequest): Promise<CommentsList> {
      const { rfiId, limit, offset, sort, filter, fields } = getCommentsRequest;
      const url = `${baseUrl}/rfis/${rfiId}/comments`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (sort) {
        searchParams.set('sort', getFlatParams(sort));
      }
      if (filter) {
        const filterParamList = constructFilterParamList(filter);
        filterParamList.forEach(([param, value]) => {
          searchParams.set(param, value);
        });
      }
      if (fields) {
        searchParams.set('fields', getFlatParams(fields));
      }
      const search = searchParams.toString();
      return fetchWithToken<CommentsList>(search ? `${url}?${search}` : url);
    },
    createComment(rfiId: RfiId, comment: string): Promise<Comment> {
      const url = `${baseUrl}/rfis/${rfiId}/comments`;
      return fetchWithToken<Comment>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify({ body: comment }),
        headers: defaultHeaders,
      });
    },
    getAttachments(getAttachmentsRequest: GetAttachmentsRequest): Promise<AttachmentList> {
      const { rfiId, limit, offset, sort, filter, fields } = getAttachmentsRequest;
      const url = `${baseUrl}/rfis/${rfiId}/attachments`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (sort) {
        searchParams.set('sort', getFlatParams(sort));
      }
      if (filter) {
        const filterParamList = constructFilterParamList(filter);
        filterParamList.forEach(([param, value]) => {
          searchParams.set(param, value);
        });
      }
      if (fields) {
        searchParams.set('fields', getFlatParams(fields));
      }
      const search = searchParams.toString();
      return fetchWithToken<AttachmentList>(search ? `${url}?${search}` : url);
    },
    createAttachment(rfiId: RfiId, attachment: CreateAttachmentRequest): Promise<Attachment> {
      const url = `${baseUrl}/rfis/${rfiId}/attachments`;
      return fetchWithToken<Attachment>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(attachment),
        headers: defaultHeaders,
      });
    },
    deleteAttachment(rfiId: RfiId, attachmentId: string) {
      const url = `${baseUrl}/rfis/${rfiId}/attachments/${attachmentId}`;
      return fetchWithToken(url, {
        method: HttpMethod.Delete,
        headers: defaultHeaders,
      });
    },
    getChangesets(getChangesetsRequest: GetChangesetsRequest): Promise<ListChangesetResponse> {
      const { rfiId, limit, offset } = getChangesetsRequest;
      const url = `${baseUrl}/rfis/${rfiId}/changesets`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }

      const search = searchParams.toString();
      return fetchWithToken<ListChangesetResponse>(search ? `${url}?${search}` : url);
    },
    createLinks(rfiId: RfiId, links: CreateLinkRequest): Promise<CreateLinkResponse> {
      const url = `${baseUrl}/rfis/${rfiId}/links`;
      return fetchWithToken<CreateLinkResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(links),
        headers: defaultHeaders,
      });
    },
    removeLinks(rfiId: RfiId, links: RemoveLinkRequestDefinition): Promise<RemoveLinkResponse> {
      const url = `${baseUrl}/rfis/${rfiId}/links:delete`;
      return fetchWithToken<RemoveLinkResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(links),
        headers: defaultHeaders,
      });
    },
  };
};

export default RFIsApi;
